import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { TextField, Autocomplete, Button } from "@mui/material";
import {
  allVideosContentValid,
  typeIsGame,
  useConfiguratorObjects,
} from "../utils/utils";
import { INITIAL_RADIUS_THEME, allThemes } from "../constants";
import ThemesDetails from "./ThemesDetails";
import {
  addElement,
  findAndRemoveOldSpawn,
  updateModalManager,
} from "../../../reducers/configurator/configuratorSlice";
import { updateMultiSelection } from "../../../reducers/configurator/configuratorSlice";
import ColorModification from "../configuratorForms/ColorModification";
import ObjectSideManagement from "./ObjectSideManagement";
import ScreenForm from "../configuratorForms/ScreenForm";

const PREFIX = "AddComponentDialogContent";

const classes = {
  root: `${PREFIX}-root`,
  buttonContainer: `${PREFIX}-buttonContainer`,
  confirmButton: `${PREFIX}-confirmButton`,
  noScrollContainer: `${PREFIX}-noScrollContainer`,
};

const AddComponentDialogContentContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      [theme.breakpoints.down("sm")]: {
        padding: "16px 0",
      },
      padding: "16px 24px",
      width: "100%",
      minWidth: "250px",
      maxWidth: "650px",
    },
    [`& .${classes.buttonContainer}`]: {
      [theme.breakpoints.down("sm")]: {
        marginLeft: "30px",
      },
    },
    [`& .${classes.confirmButton}`]: {
      position: "relative",
      left: "50%",
      transform: "translateX(-50%)",
      marginTop: "1rem",
    },
    [`& .${classes.noScrollContainer}`]: {
      [theme.breakpoints.down("sm")]: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
    },
  };
});

const AddComponentDialogContent = ({ configObjects }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.role === "ADMIN"
  );
  const layout = useSelector((state) => state.configurator.layout);
  const parentLayout = useSelector((state) => state.configurator.parentLayout);
  const selectedTile = useSelector((state) => state.configurator.selectedTile);
  const myTile = useSelector((state) =>
    selectedTile
      ? state.configurator.grid[parentLayout][selectedTile.row][
          selectedTile.column
        ]
      : null
  );

  const [selectedObject, setSelectedObject] = useState(null);
  const [presetState, setPresetState] = useState(null);
  const configuratorObjects = useConfiguratorObjects(isAdmin, configObjects);

  const handleThemeValidation = () => {
    let isDisabled = true;
    if (layout === "theme" && presetState) {
      if (
        selectedObject.value === "songAndFilter" &&
        presetState.song &&
        presetState.theme !== "songAndFilter"
      ) {
        isDisabled = false;
      } else if (selectedObject.value === "song" && presetState.preview) {
        isDisabled = false;
      } else if (
        selectedObject.value !== "song" &&
        selectedObject.value !== "songAndFilter" &&
        presetState.theme
      ) {
        isDisabled = false;
      }
    } else {
      if (
        presetState?.live &&
        (!presetState.start ||
          !presetState.end ||
          new Date(presetState.start) > new Date(presetState.end))
      ) {
        return true;
      }
      isDisabled = false;
    }
    return isDisabled;
  };

  const presetObjectState = (obj) => {
    const finalObj = obj.configuratorOptions || obj;
    const data = obj.configuratorOptions ? { ...obj } : {};
    console.log("finalObj =>", finalObj);
    if (finalObj.value === "ipad" || finalObj.value === "screen")
      setPresetState({
        autoplay: false,
        mainVideo: false,
        link: "",
        links: [],
        mute: false,
        live: false,
        loop: true,
        start: null,
        end: null,
        layout: parentLayout,
      });
    else if (typeIsGame(finalObj.value)) {
      const tmp = {
        canFieldMove: false,
      };
      if (finalObj.value === "basket2") tmp.gameContestDuration = 120;
      setPresetState(tmp);
    } else if (finalObj.value === "logo")
      setPresetState({
        file: null,
        layout: parentLayout,
      });
    else if (finalObj.isEvent)
      setPresetState({
        color: { r: 211, g: 175, b: 95, a: 1 },
        layout: parentLayout,
        ...data,
      });
    else if (finalObj.isTheme)
      setPresetState({
        song: null,
        title: "",
        description: "",
        loop: false,
        radius: INITIAL_RADIUS_THEME,
        areaMode: "radius",
        theme: finalObj.value === "song" ? null : finalObj.value,
        layout: parentLayout,
      });
    else
      setPresetState({
        iconUrl: finalObj.iconUrl,
        previewUrl: finalObj.previewUrl,
        layout: parentLayout,
        ...data,
      });
  };

  const handleConfirmObject = () => {
    const { row, column } = selectedTile;
    const finalObject = selectedObject.configuratorOptions || selectedObject;
    if (finalObject.isTheme || finalObject.value === "spawn") {
      if (finalObject.value === "spawn") dispatch(findAndRemoveOldSpawn());
      dispatch(
        addElement({
          layer: layout !== "normal" ? layout : "middle",
          data:
            layout !== "normal"
              ? presetState
              : { ...presetState, type: finalObject.value || presetState.type },
          tile: { row, column },
        })
      );
    } else if (
      finalObject.x > 0 &&
      finalObject.y > 0 &&
      finalObject.associateCoords
    ) {
      dispatch(
        updateMultiSelection({
          active: true,
          layer: layout !== "normal" ? layout : "middle",
          data: { ...presetState, type: finalObject.value || presetState.type },
          tile: { row, column },
          associateCoords: true,
          x: finalObject.x,
          y: finalObject.y,
        })
      );
    } else if (
      finalObject.isEvent ||
      (!finalObject.onFacade && finalObject.x === 0 && finalObject.y === 0)
    ) {
      if (finalObject.unlimited)
        dispatch(
          updateMultiSelection({
            layer: finalObject.isEvent ? layout : "middle",
            active: true,
            unlimited: finalObject.unlimited,
            tile: myTile.middle?.type === "spawn" ? [] : [selectedTile],
            data: {
              ...presetState,
              type: finalObject.value || presetState.type,
            },
          })
        );
      else
        dispatch(
          addElement({
            layer: layout !== "normal" ? layout : "middle",
            data: {
              ...presetState,
              type: finalObject.value || presetState.type,
            },
            tile: { row, column },
          })
        );
    } else if (
      (finalObject.x > 0 || finalObject.y > 0) &&
      !finalObject.associateCoords
    ) {
      dispatch(
        updateMultiSelection({
          active: true,
          layer:
            layout !== "normal"
              ? layout
              : presetState.hasOwnProperty("rotation")
              ? presetState.rotation
              : "middle",
          data: { ...presetState, type: finalObject.value || presetState.type },
          tile: { row, column },
          x: finalObject.x,
          y: finalObject.y,
        })
      );
    } else if (finalObject.x < 0 || finalObject.y < 0) {
      dispatch(
        updateMultiSelection({
          active: true,
          layer: presetState.rotation,
          data: { ...presetState, type: finalObject.value || presetState.type },
          tile: { row, column },
          x: finalObject.x,
          y: finalObject.y,
        })
      );
    }
    dispatch(updateModalManager({ open: false, type: null, renderer: null }));
  };

  const isItemValidAndNeedFacade = (item, itemState) => {
    let needFacade = true;
    if (
      !item ||
      ((item.value === "ipad" || item.value === "screen") &&
        ((itemState && itemState?.live && itemState?.link?.length === 0) ||
          (itemState?.live && !itemState?.start && !itemState.end) ||
          (itemState && !itemState?.live && itemState?.links?.length === 0) ||
          (itemState.links.length > 0 &&
            !allVideosContentValid(itemState.links))))
    ) {
      needFacade = false;
    }
    return needFacade;
  };

  const displayConfirmButton = () => {
    let display = false;
    if (selectedObject?.value) {
      if (
        (selectedObject.onFacade || selectedObject.isOriented) &&
        presetState.rotation
      )
        display = true;
      if (!selectedObject.onFacade && !selectedObject.isOriented)
        display = true;
    }
    return display;
  };

  return (
    <AddComponentDialogContentContainer>
      <div className={classes.root}>
        <Autocomplete
          id="select-object-to-add"
          options={
            layout === "event"
              ? configuratorObjects?.filter((item) => item.isEvent)
              : layout === "theme"
              ? configuratorObjects?.filter((item) => item.isTheme)
              : configuratorObjects?.filter(
                  (item) => !item.isEvent && !item.isTheme
                )
          }
          getOptionLabel={(option) => option.title}
          getOptionDisabled={(option) => option.disabled}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          onChange={(e, value) => {
            if (value) {
              presetObjectState(value);
              setSelectedObject(value);
            } else
              setSelectedObject({
                title: "",
                value: null,
                isOriented: false,
                onFacade: false,
                disabled: false,
              });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("configurator.autocompleteSelectObject")}
              variant="outlined"
            />
          )}
        />
        {presetState && selectedObject.value && layout === "event" && (
          <ColorModification
            element={presetState}
            handleChange={setPresetState}
          />
        )}
        {presetState &&
          selectedObject.value &&
          layout === "theme" &&
          allThemes.includes(selectedObject.value) && (
            <ThemesDetails
              selectedObject={selectedObject}
              presetState={presetState}
              setPresetState={setPresetState}
              creation
            />
          )}
        {selectedObject &&
          presetState &&
          (selectedObject?.value === "ipad" ||
            selectedObject?.value === "screen") && (
            <ScreenForm state={presetState} setState={setPresetState} />
          )}
        <div className={classes.noScrollContainer}>
          {selectedObject?.value &&
            isItemValidAndNeedFacade(selectedObject, presetState) &&
            (selectedObject.onFacade || selectedObject.isOriented) && (
              <ObjectSideManagement
                element={presetState}
                handleChange={setPresetState}
                selectedObject={selectedObject}
              />
            )}
          {displayConfirmButton() &&
            isItemValidAndNeedFacade(selectedObject, presetState) && (
              <div
                className={
                  selectedObject?.value &&
                  isItemValidAndNeedFacade(selectedObject, presetState) &&
                  (selectedObject.onFacade || selectedObject.isOriented)
                    ? classes.buttonContainer
                    : ""
                }
              >
                <Button
                  variant="contained"
                  color="primary"
                  component="span"
                  className={classes.confirmButton}
                  disabled={handleThemeValidation()}
                  onClick={(e) => {
                    handleConfirmObject();
                  }}
                >
                  {t("configurator.confirm")}
                </Button>
              </div>
            )}
        </div>
      </div>
    </AddComponentDialogContentContainer>
  );
};

export default AddComponentDialogContent;
